import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Phone, MapPin, Package, Calendar, MessageSquare,
  Trash2, X, Send, ExternalLink, User, Clock, DollarSign,
  TrendingUp, Trophy, Flame, FileText
} from 'lucide-react';
import QuoteBuilder from './QuoteBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { id: 'new',         label: 'Novo Lead',     color: 'bg-blue-500',    border: 'border-blue-500/40' },
  { id: 'contacted',   label: 'Contatado',     color: 'bg-cyan-500',    border: 'border-cyan-500/40' },
  { id: 'negotiating', label: 'Negociando',    color: 'bg-yellow-500',  border: 'border-yellow-500/40' },
  { id: 'proposal',    label: 'Proposta',      color: 'bg-purple-500',  border: 'border-purple-500/40' },
  { id: 'won',         label: 'Ganho',         color: 'bg-green-500',   border: 'border-green-500/40' },
  { id: 'lost',        label: 'Perdido',       color: 'bg-red-500',     border: 'border-red-500/40' }
];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const cleanPhone = (phone) => (phone || '').replace(/\D/g, '');

const CRMKanban = ({ showMessage }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);
  const [hoverColumn, setHoverColumn] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [quoteBuilderOpen, setQuoteBuilderOpen] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch (e) {
      console.error('Failed to load leads', e);
      showMessage?.('Erro ao carregar leads', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const handleDragStart = (lead) => setDraggedLead(lead);
  const handleDragEnd = () => { setDraggedLead(null); setHoverColumn(null); };
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    setHoverColumn(columnId);
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    setHoverColumn(null);
    if (!draggedLead || draggedLead.status === columnId) {
      setDraggedLead(null);
      return;
    }
    const previousStatus = draggedLead.status;
    // Optimistic update
    setLeads(prev => prev.map(l => l.id === draggedLead.id ? { ...l, status: columnId } : l));
    try {
      await axios.put(`${API}/leads/${draggedLead.id}`, { status: columnId });
      showMessage?.('Status atualizado!');
    } catch (err) {
      console.error('Failed to update status', err);
      // Rollback
      setLeads(prev => prev.map(l => l.id === draggedLead.id ? { ...l, status: previousStatus } : l));
      showMessage?.('Erro ao atualizar status', 'error');
    } finally {
      setDraggedLead(null);
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Excluir este lead permanentemente?')) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads(prev => prev.filter(l => l.id !== id));
      setSelectedLead(null);
      showMessage?.('Lead excluído');
    } catch (e) {
      console.error('Failed to delete lead', e);
      showMessage?.('Erro ao excluir', 'error');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    setSavingNote(true);
    try {
      const res = await axios.post(`${API}/leads/${selectedLead.id}/notes`, { text: newNote.trim() });
      setLeads(prev => prev.map(l => l.id === selectedLead.id ? res.data : l));
      setSelectedLead(res.data);
      setNewNote('');
      showMessage?.('Nota adicionada');
    } catch (e) {
      console.error('Failed to add note', e);
      showMessage?.('Erro ao adicionar nota', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const openWhatsAppForLead = (lead) => {
    const phone = cleanPhone(lead.phone);
    if (!phone) return;
    const message = `Olá ${lead.name?.split(' ')[0] || ''}! Aqui é da Rotativa Gaúcha. Vi seu interesse em ${lead.productInterest || 'nossas churrasqueiras'} e quero te ajudar.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Stats
  const stats = COLUMNS.reduce((acc, col) => {
    acc[col.id] = leads.filter(l => l.status === col.id).length;
    return acc;
  }, {});

  // Dashboard metrics
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const parseDate = (iso) => {
    try { return new Date(iso); } catch { return null; }
  };

  const leadsToday = leads.filter(l => {
    const d = parseDate(l.createdAt);
    return d && d >= startOfDay;
  }).length;

  const inProgress = leads.filter(l =>
    ['contacted', 'negotiating', 'proposal'].includes(l.status)
  ).length;

  const wonThisMonth = leads.filter(l => {
    if (l.status !== 'won') return false;
    const d = parseDate(l.updatedAt) || parseDate(l.createdAt);
    return d && d >= startOfMonth;
  }).length;

  // Conversion rate (won total vs all closed leads)
  const totalClosed = leads.filter(l => l.status === 'won' || l.status === 'lost').length;
  const totalWon = leads.filter(l => l.status === 'won').length;
  const conversionRate = totalClosed > 0 ? Math.round((totalWon / totalClosed) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-kanban">
      {/* Dashboard - Métricas principais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="crm-dashboard">
        <MetricCard
          testId="metric-new-today"
          icon={Flame}
          label="Leads novos hoje"
          value={leadsToday}
          subtitle={leadsToday === 1 ? 'lead chegando' : 'leads chegando'}
          accent="from-blue-500/20 to-blue-500/5 border-blue-500/30"
          iconColor="text-blue-400"
        />
        <MetricCard
          testId="metric-in-progress"
          icon={TrendingUp}
          label="Em andamento"
          value={inProgress}
          subtitle="precisam de follow-up"
          accent="from-amber-500/20 to-amber-500/5 border-amber-500/30"
          iconColor="text-amber-400"
        />
        <MetricCard
          testId="metric-won-month"
          icon={Trophy}
          label="Fechados no mês"
          value={wonThisMonth}
          subtitle={now.toLocaleDateString('pt-BR', { month: 'long' })}
          accent="from-green-500/20 to-green-500/5 border-green-500/30"
          iconColor="text-green-400"
        />
        <MetricCard
          testId="metric-conversion"
          icon={DollarSign}
          label="Taxa de conversão"
          value={`${conversionRate}%`}
          subtitle={totalClosed > 0 ? `${totalWon} ganhos de ${totalClosed} fechados` : 'sem fechamentos ainda'}
          accent="from-purple-500/20 to-purple-500/5 border-purple-500/30"
          iconColor="text-purple-400"
        />
      </div>

      {/* Header & Stats */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">CRM • Kanban de Leads</h2>
            <p className="text-stone-400 text-sm">{leads.length} lead(s) • Arraste os cards entre as colunas</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {COLUMNS.map(col => (
              <div key={col.id} className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${col.color}`}></span>
                <span className="text-stone-300">{col.label}</span>
                <span className="text-white font-bold">{stats[col.id] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {leads.length === 0 && (
        <div className="text-center py-16 bg-stone-800 rounded-xl border border-stone-700">
          <User className="w-12 h-12 text-stone-600 mx-auto mb-3" />
          <p className="text-stone-300 font-medium">Nenhum lead ainda</p>
          <p className="text-stone-500 text-sm">Quando alguém preencher o chat de qualificação no site, vai aparecer aqui.</p>
        </div>
      )}

      {/* Kanban Board */}
      {leads.length > 0 && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {COLUMNS.map(col => {
              const colLeads = leads.filter(l => l.status === col.id);
              const isHover = hoverColumn === col.id && draggedLead && draggedLead.status !== col.id;
              return (
                <div
                  key={col.id}
                  data-testid={`crm-column-${col.id}`}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDragLeave={() => setHoverColumn(null)}
                  onDrop={(e) => handleDrop(e, col.id)}
                  className={`w-72 flex-shrink-0 bg-stone-800/50 rounded-xl border-2 transition-all ${
                    isHover ? `${col.border} bg-stone-800` : 'border-stone-700'
                  }`}
                >
                  <div className="p-3 border-b border-stone-700 flex items-center justify-between sticky top-0 bg-stone-800/80 backdrop-blur rounded-t-xl">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${col.color}`}></span>
                      <h3 className="text-white font-semibold text-sm">{col.label}</h3>
                    </div>
                    <span className="text-stone-400 text-xs bg-stone-700 px-2 py-0.5 rounded-full">{colLeads.length}</span>
                  </div>

                  <div className="p-2 space-y-2 min-h-[200px]">
                    {colLeads.map(lead => (
                      <div
                        key={lead.id}
                        draggable
                        onDragStart={() => handleDragStart(lead)}
                        onDragEnd={handleDragEnd}
                        onClick={() => setSelectedLead(lead)}
                        data-testid={`lead-card-${lead.id}`}
                        className={`bg-stone-900 hover:bg-stone-800 border border-stone-700 hover:border-amber-500/50 rounded-lg p-3 cursor-grab active:cursor-grabbing transition-all ${
                          draggedLead?.id === lead.id ? 'opacity-40' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-white font-semibold text-sm leading-tight">{lead.name}</h4>
                          {lead.timeline === 'Quero agora!' && (
                            <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold flex-shrink-0">URGENTE</span>
                          )}
                        </div>
                        <div className="space-y-1 text-xs text-stone-400">
                          <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {lead.phone}</div>
                          {(lead.city || lead.state) && (
                            <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {lead.city}{lead.state ? `/${lead.state}` : ''}</div>
                          )}
                          {lead.productInterest && (
                            <div className="flex items-center gap-1.5"><Package className="w-3 h-3" /> {lead.productInterest}</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-700/50">
                          <span className="text-[10px] text-stone-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />{formatDate(lead.createdAt)}
                          </span>
                          {(lead.notes?.length || 0) > 0 && (
                            <span className="text-[10px] text-amber-400 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />{lead.notes.length}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {colLeads.length === 0 && (
                      <div className="text-center py-8 text-stone-600 text-xs">
                        {isHover ? 'Solta aqui!' : 'Vazio'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedLead(null)}
        >
          <div
            data-testid="lead-detail-modal"
            className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-stone-700 flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-transparent">
              <div>
                <h3 className="text-xl font-bold text-white">{selectedLead.name}</h3>
                <p className="text-stone-400 text-sm">{formatDate(selectedLead.createdAt)}</p>
              </div>
              <button
                data-testid="close-lead-modal"
                onClick={() => setSelectedLead(null)}
                className="text-stone-400 hover:text-white p-1 rounded-full hover:bg-stone-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="lead-whatsapp-btn"
                  onClick={() => openWhatsAppForLead(selectedLead)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Abrir WhatsApp do Cliente
                </button>
                <button
                  data-testid="generate-quote-btn-lead"
                  onClick={() => setQuoteBuilderOpen(true)}
                  className="bg-amber-500 hover:bg-amber-400 text-stone-900 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" /> Gerar Orçamento
                </button>
                <select
                  data-testid="lead-status-select"
                  value={selectedLead.status}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    try {
                      const res = await axios.put(`${API}/leads/${selectedLead.id}`, { status: newStatus });
                      setLeads(prev => prev.map(l => l.id === selectedLead.id ? res.data : l));
                      setSelectedLead(res.data);
                      showMessage?.('Status atualizado!');
                    } catch (err) {
                      console.error(err);
                      showMessage?.('Erro ao atualizar', 'error');
                    }
                  }}
                  className="bg-stone-800 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none"
                >
                  {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <button
                  data-testid="delete-lead-btn"
                  onClick={() => handleDeleteLead(selectedLead.id)}
                  className="ml-auto bg-red-600/20 hover:bg-red-600/30 text-red-400 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" /> Excluir
                </button>
              </div>

              {/* Lead Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoRow icon={Phone} label="Telefone" value={selectedLead.phone} />
                <InfoRow icon={MapPin} label="Localização" value={`${selectedLead.city || '-'}${selectedLead.state ? `/${selectedLead.state}` : ''}`} />
                <InfoRow icon={Package} label="Produto" value={selectedLead.productInterest || '-'} />
                <InfoRow icon={Package} label="Tamanho" value={selectedLead.size || '-'} />
                <InfoRow icon={User} label="Tipo de uso" value={selectedLead.usageType || '-'} />
                <InfoRow icon={User} label="Pessoas" value={selectedLead.peopleCount || '-'} />
                <InfoRow icon={Calendar} label="Prazo de compra" value={selectedLead.timeline || '-'} />
                <InfoRow icon={DollarSign} label="Pagamento" value={selectedLead.paymentPreference || '-'} />
              </div>

              {/* Notes */}
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-amber-500" />
                  Histórico de notas ({selectedLead.notes?.length || 0})
                </h4>

                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                  {(selectedLead.notes || []).slice().reverse().map((note, idx) => (
                    <div key={idx} className="bg-stone-800 border border-stone-700 rounded-lg p-3">
                      <p className="text-stone-200 text-sm whitespace-pre-line">{note.text}</p>
                      <p className="text-stone-500 text-xs mt-1">{formatDate(note.createdAt)}</p>
                    </div>
                  ))}
                  {(!selectedLead.notes || selectedLead.notes.length === 0) && (
                    <p className="text-stone-500 text-sm italic">Nenhuma nota ainda. Adicione abaixo após cada contato.</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <textarea
                    data-testid="new-note-input"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Ex: Liguei, falou que vai pensar e retorna sexta-feira"
                    rows={2}
                    className="flex-1 bg-stone-800 border border-stone-600 text-white rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none resize-none"
                  />
                  <button
                    data-testid="save-note-btn"
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || savingNote}
                    className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-stone-900 px-4 rounded-lg font-semibold flex items-center gap-1"
                  >
                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Builder Modal */}
      {quoteBuilderOpen && selectedLead && (
        <QuoteBuilder
          lead={selectedLead}
          onClose={() => setQuoteBuilderOpen(false)}
          showMessage={showMessage}
        />
      )}
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="bg-stone-800 border border-stone-700 rounded-lg p-3">
    <div className="flex items-center gap-2 text-stone-500 text-xs mb-1">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-white font-medium text-sm">{value}</div>
  </div>
);

const MetricCard = ({ testId, icon: Icon, label, value, subtitle, accent, iconColor }) => (
  <div
    data-testid={testId}
    className={`bg-gradient-to-br ${accent} border rounded-xl p-4 transition-transform hover:scale-[1.02]`}
  >
    <div className="flex items-start justify-between mb-2">
      <span className="text-stone-300 text-xs font-medium uppercase tracking-wide">{label}</span>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div className="text-white text-3xl font-bold leading-none mb-1">{value}</div>
    <div className="text-stone-400 text-xs">{subtitle}</div>
  </div>
);

export default CRMKanban;
